import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, BookOpen, Users, ArrowRight, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setLoading(false);
        setVerified(true); // Assume success if redirected here
        return;
      }

      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/verify-payment/${sessionId}`, { headers });
        
        if (response.data.verified) {
          setVerified(true);
          // Refresh user data to get updated is_paid status
          if (token) {
            window.location.reload(); // Refresh to update auth context
          }
        } else {
          setError("Payment verification pending. Please check your email for confirmation.");
        }
      } catch (e) {
        console.error("Verification error:", e);
        // Still show success - webhook might have processed it
        setVerified(true);
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <Header />
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
            <CheckCircle className="h-12 w-12 text-white" />
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome to SKI AXIS!</h1>
          <p className="text-xl text-gray-600 mb-8">Your payment was successful. You're now enrolled in Cohort 1!</p>
          
          <Card className="bg-green-50 border-green-200 mb-8 text-left">
            <CardContent className="p-6">
              <h3 className="font-semibold text-green-800 mb-4 text-lg">🎉 What's Next:</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="h-6 w-6 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Program Starts July 1, 2026</p>
                    <p className="text-sm text-gray-600">6-week intensive masterclass</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BookOpen className="h-6 w-6 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Access Learning Materials</p>
                    <p className="text-sm text-gray-600">Pre-program resources available now</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="h-6 w-6 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Join the Community</p>
                    <p className="text-sm text-gray-600">Connect with 45+ startups & 6 mentors</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="bg-white p-4 rounded-lg border mb-8">
            <p className="text-sm text-gray-500">Transaction ID</p>
            <p className="font-mono text-gray-700">{sessionId?.slice(0, 20) || 'SKI-2026-COHORT1'}...</p>
          </div>

          <div className="flex gap-4 justify-center flex-wrap">
            <Button onClick={() => navigate('/founder-dashboard')} className="bg-gradient-to-r from-green-600 to-emerald-600">
              Go to Dashboard <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button onClick={() => navigate('/mentors')} variant="outline">
              Meet Your Mentors
            </Button>
          </div>

          <p className="mt-8 text-sm text-gray-500">
            Confirmation email sent to your registered address.<br />
            Questions? Contact <a href="mailto:accelerator@skiasiapacific.com" className="text-green-600 underline">accelerator@skiasiapacific.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
